import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { providerApi } from '../../services/api';
import MyAudience from './MyAudience';

const isoDate = (d) => d.toISOString().slice(0, 10);

function Kpi({ label, value }) {
  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, flex: 1, minWidth: 130 }}>
      <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.66rem' }}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700}>{value}</Typography>
    </Paper>
  );
}

/**
 * Pro analytics (design §8): range selector, KPI row with the explicit metric
 * split (leads received vs confirmed bookings vs completed jobs), views chart,
 * funnel chart, and the audience section absorbed from MyAudience. Renders
 * zeros gracefully — revenue legitimately reads 0 until the booking/payment
 * implementation populates transaction attribution (contract-owned).
 */
export default function AnalyticsPage({ providerData, onNavigateToOrders }) {
  const { t, i18n } = useTranslation();
  const [rangeDays, setRangeDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (days) => {
    setLoading(true);
    setError('');
    try {
      const to = new Date();
      const from = new Date(Date.now() - (days - 1) * 24 * 60 * 60 * 1000);
      const result = await providerApi.getMyAnalytics(isoDate(from), isoDate(to));
      setData(result);
    } catch (e) {
      setError(t('analyticsPage.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(rangeDays);
  }, [rangeDays, load]);

  const totals = data?.totals;
  const revenueText = totals && Object.keys(totals.revenueByCurrency || {}).length > 0
    ? Object.entries(totals.revenueByCurrency)
        .map(([currency, amount]) => `${Number(amount).toLocaleString()} ${currency}`)
        .join(' · ')
    : '0';
  const ratingText = totals?.rating?.average != null
    ? `${totals.rating.average.toFixed(1)} (${totals.rating.count})`
    : '—';

  const series = (data?.series || []).map((day) => ({
    ...day,
    date: new Date(day.date).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', { day: 'numeric', month: 'numeric' }),
  }));
  const hasAnyData = (data?.series || []).some((d) => d.views || d.leads || d.confirmed || d.completed);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
        <Typography variant="h5" fontWeight={700}>{t('analyticsPage.title')}</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={rangeDays}
          onChange={(e, value) => value && setRangeDays(value)}
        >
          <ToggleButton value={7}>{t('analyticsPage.range7')}</ToggleButton>
          <ToggleButton value={30}>{t('analyticsPage.range30')}</ToggleButton>
          <ToggleButton value={90}>{t('analyticsPage.range90')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
            <Kpi label={t('analyticsPage.kpiViews')} value={totals?.views ?? 0} />
            <Kpi label={t('analyticsPage.kpiLeads')} value={totals?.leads ?? 0} />
            <Kpi label={t('analyticsPage.kpiConfirmed')} value={totals?.confirmedBookings ?? 0} />
            <Kpi label={t('analyticsPage.kpiCompleted')} value={totals?.completedJobs ?? 0} />
            <Kpi label={t('analyticsPage.kpiRevenue')} value={revenueText} />
            <Kpi label={t('analyticsPage.kpiRating')} value={ratingText} />
          </Box>

          {!hasAnyData && (
            <Alert severity="info" sx={{ mb: 2.5 }}>{t('analyticsPage.emptyHint')}</Alert>
          )}

          {/* Recharts does not support RTL axes — charts render in an LTR island with translated labels. */}
          <Box dir="ltr">
            <Card sx={{ mb: 2.5 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('analyticsPage.chartViewsTitle')}</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={11} />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <Tooltip />
                    <Line type="monotone" dataKey="views" name={t('analyticsPage.kpiViews')} stroke="#6D28D9" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2.5 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('analyticsPage.chartFunnelTitle')}</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={11} />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="leads" name={t('analyticsPage.kpiLeads')} fill="#8B5CF6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="confirmed" name={t('analyticsPage.kpiConfirmed')} fill="#10B981" radius={[3, 3, 0, 0]} />
                    <Line type="monotone" dataKey="completed" name={t('analyticsPage.kpiCompleted')} stroke="#F59E0B" dot={false} strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Box>

          {/* Audience section absorbed from the former MyAudience tab (design §3). */}
          <MyAudience providerData={providerData} onNavigateToOrders={onNavigateToOrders} />
        </>
      )}
    </Box>
  );
}
